import { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FormContainerControlled,
  Typography,
} from "components";
import { HookForm, OTPInput } from "pages/Components";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { authGenerateOTPService } from "services";
import { i18n } from "translations";
import { getRequiredMessage } from "utils";
import * as Yup from "yup";

import { useSignIn } from "./hooks";

interface EmailWithOTPProps {
  onLogin: ReturnType<typeof useSignIn>["handleLoginWithOTP"];
  loading?: boolean;
}

export enum Step {
  StepEmail = 1,
  StepOTP = 2,
}

const DEFAULT_TIMEOUT = 60000;

const FormSchema = Yup.object({
  email: Yup.string()
    .email(i18n.t("emailIsInvalid"))
    .required(getRequiredMessage(i18n.t("email"))),
  password: Yup.string()
    .min(6)
    .required(getRequiredMessage(i18n.t("password"))),
});
type TForm = Yup.InferType<typeof FormSchema>;

const ResendOTP: React.FC<{ email: string }> = ({ email }) => {
  const [timeout, setTimeout] = useState(DEFAULT_TIMEOUT);
  const { mutate: generateOTP } = useMutation({
    mutationKey: "authGenerateOTPService",
    mutationFn: authGenerateOTPService,
    onSuccess: () => {
      setTimeout(DEFAULT_TIMEOUT);
    },
  });

  useEffect(() => {
    const intervalFn = () => {
      setTimeout((prev) => (prev === 0 ? 0 : prev - 1000));
    };
    const intervalObj = setInterval(intervalFn, 1000);

    return () => {
      clearInterval(intervalObj);
    };
  }, []);

  // handle timeout resend otp
  const onResendOTP = () => {
    if (timeout === 0) {
      generateOTP(email);
    }
  };
  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a href="#" onClick={onResendOTP}>
        {i18n.t("tryAgainAfter")}
      </a>{" "}
      <span style={{ color: "blue" }}>{timeout / 1000}s</span>
    </div>
  );
};

const EmailWithOTP: React.FC<EmailWithOTPProps> = ({ onLogin, loading }) => {
  const [step, setStep] = useState<Step>(Step.StepEmail);
  const [otp, setOtp] = useState("");
  const [csrfToken, setCsrfToken] = useState("");

  const { mutate: generateOTP } = useMutation({
    mutationKey: "authGenerateOTPService",
    mutationFn: authGenerateOTPService,
    onSuccess: (res) => {
      setCsrfToken(res?.data?.csrfToken);
    },
  });

  const methods = useForm<TForm>({
    resolver: yupResolver(FormSchema) as never,
    defaultValues: {},
  });
  const onNextStep = () => {
    if (step === Step.StepEmail) {
      setStep(Step.StepOTP);
      generateOTP(methods.getValues("email"));
    }
    if (step === Step.StepOTP && otp?.length === 6) {
      onLogin(methods.getValues("email"), otp, csrfToken);
    }
  };
  const onBackStep = () => {
    if (step === Step.StepOTP) {
      setStep(Step.StepEmail);
      setOtp("");
    }
  };
  return (
    <FormContainerControlled methods={methods} onSubmit={onNextStep}>
      <HookForm
        fields={[
          {
            col: 12,
            label: "Email",
            name: "email",
            type: "text",
            required: true,
            onHide: () => !(step === Step.StepEmail),
          },
          {
            col: 12,
            type: "custom",

            renderCustomNode() {
              return (
                <Typography fontWeight={400} type="TextSM" color="grey.600">
                  {i18n.t("oTPHasBeenSentToEmail")}{" "}
                  <strong>{methods?.getValues("email") || ""}</strong>
                </Typography>
              );
            },
            onHide: () => !(step === Step.StepOTP),
          },
          {
            col: 12,
            name: "email",
            type: "component",
            component: OTPInput,
            componentProps: {
              length: 6,
              separator: "-",
              value: otp,
              onChange: setOtp,
            },
            onHide: () => !(step === Step.StepOTP),
          },
          {
            col: 12,
            type: "component",
            component: ResendOTP,
            componentProps: {
              email: methods?.getValues("email") || "",
            },
            onHide: () => !(step === Step.StepOTP),
          },
          {
            col: 12,
            type: "button",
            buttonProps: {
              labelI18n: "next",
              fullWidth: true,
              variant: "outlined",
              loading,
              endIcon: <ChevronRightIcon />,
              onClick: async () => {
                await methods.trigger();
                if (!methods.getFieldState("email").invalid) {
                  onNextStep();
                }
              },
            },
          },
          {
            col: 12,
            type: "button",
            buttonProps: {
              labelI18n: "back",
              fullWidth: true,
              variant: "text",
              color: "inherit",
              startIcon: <ChevronLeftIcon />,
              onClick: onBackStep,
            },
            onHide: () => !(step === Step.StepOTP),
          },
        ]}
      />
    </FormContainerControlled>
  );
};

export default EmailWithOTP;
